// Normalization function to handle different county name formats
export const normalizeCountyLabel = (label) => {
  if (!label) return "";
  return label
    .toUpperCase()
    .replace(
      /\s+(COUNTY|TOWN|BOROUGH|PARISH|CENSUS AREA|MUNICIPIO|PLANNING REGION|CITY AND BOROUGH|CITY|UNORGANIZED TERRITORY|MUNICIPALITY)\s*$/,
      ""
    )
    .replace(/[.']/g, "")
    .trim();
};

export const getCountyObject = ({
  rawCounty,
  allCounties,
  normalizeCountyLabel,
}) => {
  if (!rawCounty) return null;

  if (typeof rawCounty === "object" && rawCounty.label) {
    return rawCounty;
  }

  const normalizedSearch = normalizeCountyLabel(rawCounty);
  for (const state in allCounties) {
    const found = allCounties[state].find(
      (county) => normalizeCountyLabel(county.label) === normalizedSearch
    );
    if (found) return found;
  }

  return null;
};

export const getCountySourceFromPwd = ({
  formStateValues,
  linkingFieldName,
  msaFieldName,
  filterByMsa = false,
}) => {
  if (
    !formStateValues._blsCounties ||
    (linkingFieldName && !formStateValues[linkingFieldName])
  ) {
    return null; // Indicates that the default source should be used
  }

  try {
    const pwdCounties = JSON.parse(formStateValues._blsCounties);
    if (!pwdCounties) return null;

    if (filterByMsa) {
      // PERM-style logic: Filter by a specific MSA
      const blsArea = formStateValues[msaFieldName];
      const msaKey = blsArea
        ? Object.keys(pwdCounties).find(
            (k) => k.trim().toUpperCase() === blsArea.trim().toUpperCase()
          )
        : undefined;
      if (msaKey) {
        return pwdCounties[msaKey] || {};
      }
      return {}; // No matching MSA key, so no valid counties from this source
    } else {
      // H2B-style logic: Flatten all counties from all MSAs
      return Object.values(pwdCounties).reduce((acc, msa) => {
        Object.entries(msa).forEach(([state, stateCounties]) => {
          if (!acc[state]) {
            acc[state] = [];
          }
          acc[state].push(...stateCounties);
        });
        return acc;
      }, {});
    }
  } catch (e) {
    console.error("Error parsing _blsCounties in getCountySourceFromPwd", e);
    return null; // Indicates fallback to default source on error
  }
};

export const getStateSelectionOptions = ({
  formApi,
  counties,
  msaFieldName,
  multiSiteField,
  multiSiteValue,
  linkingFieldName,
  defaultStates,
}) => {
  let onlyOneState = false;
  let values = [];
  const formStateValues = formApi.getState().values;
  const blsArea = formStateValues[msaFieldName];

  if (formStateValues[multiSiteField] === multiSiteValue) {
    return [true, ["N/A"]];
  }

  // Prioritize _blsCounties if available and linked
  if (formStateValues._blsCounties && formStateValues[linkingFieldName]) {
    try {
      const parsed = JSON.parse(formStateValues._blsCounties);
      const msaKey = blsArea
        ? Object.keys(parsed).find(
            (k) => k.trim().toUpperCase() === blsArea.trim().toUpperCase()
          )
        : undefined;
      if (msaKey) {
        const msaData = parsed[msaKey];
        values = Object.keys(msaData); // The keys are the state names
        if (values.length === 1) {
          onlyOneState = true;
        }
        return [onlyOneState, values];
      }
    } catch (e) {
      console.error(
        "Could not parse _blsCounties for State selection, using fallback.",
        e
      );
      // Fall through to default logic
    }
  }

  // Fallback logic
  if (!blsArea || !formStateValues[linkingFieldName]) {
    return [onlyOneState, defaultStates];
  }
  const restrictedStatesSet = new Set();
  Object.keys(counties).forEach((state) => {
    (counties[state] || []).forEach((county) => {
      if (county.bls && blsArea.toUpperCase() === county.bls.toUpperCase()) {
        restrictedStatesSet.add(state);
      }
    });
  });
  values = Array.from(restrictedStatesSet);
  if (values.length === 1) {
    onlyOneState = true;
  }
  return [onlyOneState, values];
};
